import React, { useRef, useState, useEffect } from "react";
import {
  Avatar,
  AvatarBadge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Center,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListIcon,
  ListItem,
  Progress,
  Stack,
  Text,
  useToast,
  VStack,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Switch,
  Select,
  useColorMode,
  Checkbox,
  CheckboxGroup,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import {
  FiEdit,
  FiUser,
  FiLock,
  FiShield,
  FiEye,
  FiSettings,
  FiEyeOff,
  FiCheck,
  FiX,
  FiCamera,
  FiMail,
  FiPhone,
} from "react-icons/fi";
import client from "../../helpers/Client";
import { useLoaderData, useNavigate, useRevalidator } from "react-router-dom";
import urlToId from "../../helpers/UrlToId";
import getFullName from "../../helpers/FullName";
import SaveButton from "../../components/buttons/SaveButton";
import { useTranslation } from "react-i18next";
import { clearAppCache } from "../../helpers/Client";

export async function profileLoader() {
  const resp = await client.get("/users/?is_me=true");
  return resp.data[0];
}

// Şifre gücü hesaplama fonksiyonu
const calculatePasswordStrength = (password) => {
  let score = 0;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  Object.values(checks).forEach((check) => {
    if (check) score += 20;
  });

  return { score, checks };
};

// Şifre gücü renk ve metin belirleme
const getPasswordStrengthInfo = (score) => {
  if (score < 40) return { color: "red.500", text: "Çok Zayıf", bg: "red.100" };
  if (score < 60)
    return { color: "orange.500", text: "Zayıf", bg: "orange.100" };
  if (score < 80)
    return { color: "yellow.500", text: "Orta", bg: "yellow.100" };
  if (score < 100) return { color: "blue.500", text: "Güçlü", bg: "blue.100" };
  return { color: "green.500", text: "Çok Güçlü", bg: "green.100" };
};

export default function UserProfileEdit() {
  const { i18n } = useTranslation();
  const { colorMode, toggleColorMode } = useColorMode();
  const fileInputRef = useRef(null);
  const revalidator = useRevalidator();
  const user = useLoaderData();
  const userId = urlToId(user.url);
  const avatar = user.employee?.avatar_small;
  const name = getFullName(user);
  const navigate = useNavigate();
  const toast = useToast();
  const [language, setLanguage] = useState(() => {
    try {
      return (
        window.localStorage.getItem("settings.language") ||
        i18n.language ||
        "tr"
      );
    } catch (_) {
      return i18n.language || "tr";
    }
  });
  const [autoRefreshTickets, setAutoRefreshTickets] = useState(() => {
    try {
      return (
        window.localStorage.getItem("settings.autoRefreshTickets") === "true"
      );
    } catch (_) {
      return false;
    }
  });
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(() => {
    try {
      return parseInt(
        window.localStorage.getItem("settings.autoRefreshInterval") || "5",
        10
      );
    } catch (_) {
      return 5;
    }
  });
  const [disableCache, setDisableCache] = useState(() => {
    try {
      return window.localStorage.getItem("settings.disableCache") === "true";
    } catch (_) {
      return false;
    }
  });
  const [cacheTTL, setCacheTTL] = useState(() => {
    try {
      return parseInt(
        window.localStorage.getItem("settings.cacheTTL") || "0",
        10
      );
    } catch (_) {
      return 0;
    }
  });
  const [lowDataMode, setLowDataMode] = useState(() => {
    try {
      return window.localStorage.getItem("settings.lowDataMode") === "true";
    } catch (_) {
      return false;
    }
  });
  const [showBadge, setShowBadge] = useState(() => {
    try {
      return (
        window.localStorage.getItem("settings.notifications.showBadge") !==
        "false"
      );
    } catch (_) {
      return true;
    }
  });
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      return (
        window.localStorage.getItem("settings.notifications.sound") === "true"
      );
    } catch (_) {
      return false;
    }
  });
  const [quietEnabled, setQuietEnabled] = useState(() => {
    try {
      return (
        window.localStorage.getItem(
          "settings.notifications.quietHours.enabled"
        ) === "true"
      );
    } catch (_) {
      return false;
    }
  });
  const [quietStart, setQuietStart] = useState(() => {
    try {
      return (
        window.localStorage.getItem(
          "settings.notifications.quietHours.start"
        ) || "22:00"
      );
    } catch (_) {
      return "22:00";
    }
  });
  const [quietEnd, setQuietEnd] = useState(() => {
    try {
      return (
        window.localStorage.getItem("settings.notifications.quietHours.end") ||
        "07:00"
      );
    } catch (_) {
      return "07:00";
    }
  });
  const [pageSize, setPageSize] = useState(() => {
    try {
      return parseInt(
        window.localStorage.getItem("settings.list.pageSize") || "0",
        10
      );
    } catch (_) {
      return 0;
    }
  });
  const [defaultSort, setDefaultSort] = useState(() => {
    try {
      return (
        window.localStorage.getItem("settings.list.defaultSort") ||
        "created_at:desc"
      );
    } catch (_) {
      return "created_at:desc";
    }
  });
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = window.localStorage.getItem("settings.list.visibleColumns");
      return raw
        ? JSON.parse(raw)
        : ["id", "title", "customer", "state", "created_at"];
    } catch (_) {
      return ["id", "title", "customer", "state", "created_at"];
    }
  });
  const [rememberFilters, setRememberFilters] = useState(() => {
    try {
      return (
        window.localStorage.getItem("settings.list.rememberFilters") === "true"
      );
    } catch (_) {
      return false;
    }
  });
  const [fontSize, setFontSize] = useState(() => {
    try {
      return (
        window.localStorage.getItem("settings.accessibility.fontSize") ||
        "medium"
      );
    } catch (_) {
      return "medium";
    }
  });
  const [darkMode, setDarkMode] = useState(() => {
    try {
      return window.localStorage.getItem("darkMode") === "true";
    } catch (_) {
      return false;
    }
  });
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifSms, setNotifSms] = useState(true);
  const [notifTriggers, setNotifTriggers] = useState([
    "ticket_created",
    "status_changed",
    "assignment",
    "completed",
  ]);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifTesting, setNotifTesting] = useState(false);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFAChannel, setTwoFAChannel] = useState("sms");
  const [view, setView] = useState("profil");
  const resetAppSettings = () => {
    try {
      const ls = window.localStorage;
      [
        "settings.language",
        "settings.autoRefreshTickets",
        "settings.autoRefreshInterval",
        "settings.disableCache",
        "settings.cacheTTL",
        "settings.lowDataMode",
        "settings.notifications.showBadge",
        "settings.notifications.sound",
        "settings.notifications.quietHours.enabled",
        "settings.notifications.quietHours.start",
        "settings.notifications.quietHours.end",
        "settings.list.pageSize",
        "settings.list.defaultSort",
        "settings.list.visibleColumns",
        "settings.list.rememberFilters",
        "settings.accessibility.fontSize",
      ].forEach((k) => {
        try {
          ls.removeItem(k);
        } catch {}
      });
    } catch {}
    setLanguage("tr");
    setAutoRefreshTickets(false);
    setAutoRefreshInterval(5);
    setDisableCache(false);
    setCacheTTL(0);
    setLowDataMode(false);
    setShowBadge(true);
    setSoundEnabled(false);
    setQuietEnabled(false);
    setQuietStart("22:00");
    setQuietEnd("07:00");
    setPageSize(0);
    setDefaultSort("created_at:desc");
    setVisibleCols(["id", "title", "customer", "state", "created_at"]);
    setRememberFilters(false);
    setFontSize("medium");
    toast({
      title: "Ayarlar sıfırlandı",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Şifre değiştirme state'leri
  const [oldPassword, setOldPassword] = useState("");
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    checks: {},
  });
  const [errorData, setErrorData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [trustedLoading, setTrustedLoading] = useState(false);

  // Kullanıcı bilgileri state'leri
  const [firstName, setFirstName] = useState(user.first_name || "");
  const [lastName, setLastName] = useState(user.last_name || "");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(user.phone || "");

  // Şifre gücü hesaplama
  useEffect(() => {
    if (password1) {
      setPasswordStrength(calculatePasswordStrength(password1));
    } else {
      setPasswordStrength({ score: 0, checks: {} });
    }
  }, [password1]);

  useEffect(() => {
    try {
      i18n.changeLanguage(language);
      window.localStorage.setItem("settings.language", language);
    } catch (_) {}
  }, [language, i18n]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.autoRefreshTickets",
        String(!!autoRefreshTickets)
      );
    } catch (_) {}
  }, [autoRefreshTickets]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.autoRefreshInterval",
        String(parseInt(autoRefreshInterval || 5, 10))
      );
    } catch (_) {}
  }, [autoRefreshInterval]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.disableCache",
        String(!!disableCache)
      );
    } catch (_) {}
  }, [disableCache]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.cacheTTL",
        String(parseInt(cacheTTL || 0, 10))
      );
    } catch (_) {}
  }, [cacheTTL]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.lowDataMode",
        String(!!lowDataMode)
      );
    } catch (_) {}
  }, [lowDataMode]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.notifications.showBadge",
        String(!!showBadge)
      );
    } catch (_) {}
  }, [showBadge]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.notifications.sound",
        String(!!soundEnabled)
      );
    } catch (_) {}
  }, [soundEnabled]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.notifications.quietHours.enabled",
        String(!!quietEnabled)
      );
    } catch (_) {}
  }, [quietEnabled]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.notifications.quietHours.start",
        quietStart
      );
    } catch (_) {}
  }, [quietStart]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.notifications.quietHours.end",
        quietEnd
      );
    } catch (_) {}
  }, [quietEnd]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.list.pageSize",
        String(parseInt(pageSize || 0, 10))
      );
    } catch (_) {}
  }, [pageSize]);
  useEffect(() => {
    try {
      window.localStorage.setItem("settings.list.defaultSort", defaultSort);
    } catch (_) {}
  }, [defaultSort]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.list.visibleColumns",
        JSON.stringify(visibleCols)
      );
    } catch (_) {}
  }, [visibleCols]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "settings.list.rememberFilters",
        String(!!rememberFilters)
      );
    } catch (_) {}
  }, [rememberFilters]);
  useEffect(() => {
    try {
      window.localStorage.setItem("settings.accessibility.fontSize", fontSize);
    } catch (_) {}
  }, [fontSize]);
  useEffect(() => {
    try {
      const v =
        fontSize ||
        window.localStorage.getItem("settings.accessibility.fontSize") ||
        "medium";
      const size = v === "small" ? "14px" : v === "large" ? "18px" : "16px";
      document.documentElement.style.fontSize = size;
    } catch (_) {}
  }, [fontSize]);

  const applyInvertDarkMode = () => {
    try {
      document.documentElement.style.filter =
        "invert(1) hue-rotate(180deg) brightness(1) contrast(0.9)";
      document.querySelectorAll("img, video, iframe").forEach((el) => {
        el.style.filter = "invert(1) hue-rotate(180deg)";
      });
    } catch (_) {}
  };
  const removeInvertDarkMode = () => {
    try {
      document.documentElement.style.filter = "";
      document.querySelectorAll("img, video, iframe").forEach((el) => {
        el.style.filter = "";
      });
    } catch (_) {}
  };
  const setDarkPreference = (enabled) => {
    setDarkMode(enabled);
    try {
      window.localStorage.setItem("darkMode", String(enabled));
    } catch (_) {}
    if (enabled) applyInvertDarkMode();
    else removeInvertDarkMode();
  };
  useEffect(() => {
    if (darkMode) applyInvertDarkMode();
    else removeInvertDarkMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await client.get(
          `/users/${userId}/notification_settings/`
        );
        const d = resp.data || {};
        setNotifEmail(!!d.email_enabled);
        setNotifSms(!!d.sms_enabled);
        setNotifTriggers(Array.isArray(d.triggers) ? d.triggers : []);
      } catch (_) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveNotificationSettings = async () => {
    try {
      setNotifSaving(true);
      await client.put(`/users/${userId}/notification_settings/`, {
        email_enabled: !!notifEmail,
        sms_enabled: !!notifSms,
        triggers: notifTriggers,
      });
      toast({
        title: "Bildirim ayarları kaydedildi",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "Bildirim ayarları kaydedilemedi",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setNotifSaving(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const resp = await client.get(`/users/${userId}/security_settings/`);
        const d = resp.data || {};
        setTwoFAEnabled(!!d.two_factor_enabled);
        setTwoFAChannel(d.two_factor_channel === "email" ? "email" : "sms");
      } catch (_) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSecuritySettings = async () => {
    try {
      await client.put(`/users/${userId}/security_settings/`, {
        two_factor_enabled: !!twoFAEnabled,
        two_factor_channel: twoFAChannel,
      });
      toast({
        title: "Güvenlik ayarları kaydedildi",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "Güvenlik ayarları kaydedilemedi",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePasswordReset = async () => {
    if (password1 !== password2) {
      toast({
        title: "Hata",
        description: "Yeni şifreler eşleşmiyor",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (passwordStrength.score < 60) {
      toast({
        title: "Zayıf Şifre",
        description: "Lütfen daha güçlü bir şifre seçin",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    const requestBody = {
      old_password: oldPassword,
      new_password: password1,
      new_password_2: password2,
    };

    try {
      await client.put(`/users/${userId}/set_password/`, requestBody);
      toast({
        title: "Başarılı",
        description: "Şifreniz başarıyla değiştirildi",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setOldPassword("");
      setPassword1("");
      setPassword2("");
      setErrorData({});
    } catch (error) {
      setErrorData(error.response?.data || {});
      toast({
        title: "Hata",
        description: "Şifre değiştirme işlemi başarısız",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    setIsLoading(true);
    try {
      await client.put(`/users/${userId}/`, {
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone: phone,
      });
      toast({
        title: "Başarılı",
        description: "Profil bilgileriniz güncellendi",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      revalidator.revalidate();
    } catch (error) {
      toast({
        title: "Hata",
        description: "Profil güncelleme işlemi başarısız",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetAvatar = (file) => {
    const formData = new FormData();
    formData.append("avatar", file);
    client
      .put(`/users/${userId}/set_avatar/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        toast({
          title: "Başarılı",
          description: "Profil fotoğrafınız güncellendi",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        revalidator.revalidate();
      })
      .catch(() => {
        toast({
          title: "Hata",
          description: "Fotoğraf yükleme işlemi başarısız",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });
  };

  const handleEditClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleSetAvatar(selectedFile);
    }
  };

  const strengthInfo = getPasswordStrengthInfo(passwordStrength.score);

  const notifChannelsSummary = () => {
    if (notifEmail && notifSms) return "E-posta ve SMS açık";
    if (notifEmail && !notifSms) return "Sadece e-posta açık";
    if (!notifEmail && notifSms) return "Sadece SMS açık";
    return "Bildirim kanalları kapalı";
  };

  const notifTriggersSummary = () => {
    const count = Array.isArray(notifTriggers) ? notifTriggers.length : 0;
    if (!count) return "Hiçbir tetikleyici seçili değil";
    if (count === 1) return "1 tetikleyici seçili";
    return count + " tetikleyici seçili";
  };

  const selectAllTriggers = () => {
    setNotifTriggers([
      "ticket_created",
      "status_changed",
      "assignment",
      "completed",
      "cancelled",
      "appointment_set",
      "appointment_updated",
      "appointment_cancelled",
      "incomplete_tickets",
    ]);
  };

  const selectCriticalTriggers = () => {
    setNotifTriggers([
      "status_changed",
      "completed",
      "cancelled",
      "incomplete_tickets",
    ]);
  };

  const clearTriggers = () => {
    setNotifTriggers([]);
  };

  const fetchTrustedDevices = async () => {
    try {
      setTrustedLoading(true);
      const resp = await client.get(`/users/${userId}/trusted_devices/`);
      setTrustedDevices(resp.data || []);
    } catch (e) {
      setTrustedDevices([]);
    } finally {
      setTrustedLoading(false);
    }
  };

  useEffect(() => {
    fetchTrustedDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeTrusted = async (fp) => {
    try {
      await client.delete(`/users/${userId}/trusted_devices/`, { data: { fingerprint: fp } });
      toast({ title: "Cihaz kaldırıldı", status: "success", duration: 2500, isClosable: true });
      fetchTrustedDevices();
    } catch (e) {
      toast({ title: "Cihaz kaldırılamadı", status: "error", duration: 2500, isClosable: true });
    }
  };

  const sendTestNotification = async () => {
    try {
      setNotifTesting(true);
      const resp = await client.post(
        `/users/${userId}/send_test_notification/`
      );
      const data = resp.data || {};
      if (data.email_sent && data.sms_sent) {
        toast({
          title: "Test bildirimi e-posta ve SMS olarak gönderildi",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      } else if (data.email_sent) {
        toast({
          title: "Test bildirimi e-posta olarak gönderildi",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      } else if (data.sms_sent) {
        toast({
          title: "Test bildirimi SMS olarak gönderildi",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Test bildirimi gönderilemedi, kanal bulunamadı",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Test bildirimi gönderilemedi";
      toast({ title: msg, status: "error", duration: 4000, isClosable: true });
    } finally {
      setNotifTesting(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Box maxW="6xl" mx="auto" px={4}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box textAlign="center">
            <Heading size="xl" color="gray.700" mb={2}>
              Hesap Ayarları
            </Heading>
            <Text color="gray.600">
              Profil bilgilerinizi ve güvenlik ayarlarınızı yönetin
            </Text>
          </Box>

          <HStack justify="center" spacing={3}>
            <Button
              size="sm"
              colorScheme="blue"
              variant={view === "profil" ? "solid" : "outline"}
              onClick={() => setView("profil")}
            >
              Profil
            </Button>
            <Button
              size="sm"
              variant={view === "sifre" ? "solid" : "outline"}
              onClick={() => setView("sifre")}
            >
              Şifre
            </Button>
            <Button
              size="sm"
              variant={view === "uygulama" ? "solid" : "outline"}
              onClick={() => setView("uygulama")}
            >
              Uygulama
            </Button>
            <Button
              size="sm"
              variant={view === "guvenlik" ? "solid" : "outline"}
              onClick={() => setView("guvenlik")}
            >
              Güvenlik
            </Button>
          </HStack>

          <Box display={view === "profil" ? "block" : "none"}>
            <Box display={view === "guvenlik" ? "block" : "none"}>
              <Card>
                <CardHeader>
                  <HStack>
                    <Icon as={FiUser} color="blue.500" />
                    <Heading size="md">Profil Bilgileri</Heading>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <VStack spacing={6}>
                    {/* Avatar Section */}
                    <Center>
                      <Box position="relative">
                        <Avatar src={avatar} size="2xl" name={name}>
                          <AvatarBadge
                            as={IconButton}
                            size="sm"
                            rounded="full"
                            top="-10px"
                            colorScheme="blue"
                            aria-label="Fotoğraf değiştir"
                            icon={<FiCamera />}
                            onClick={handleEditClick}
                            cursor="pointer"
                          />
                        </Avatar>
                        <input
                          type="file"
                          accept="image/png, image/jpeg, image/jpg"
                          style={{ display: "none" }}
                          ref={fileInputRef}
                          onChange={handleFileChange}
                        />
                      </Box>
                    </Center>

                    {/* User Info Form */}
                    <Grid
                      templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                      gap={6}
                      w="full"
                    >
                      <GridItem>
                        <FormControl>
                          <FormLabel>Ad</FormLabel>
                          <Input
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="Adınız"
                          />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Soyad</FormLabel>
                          <Input
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Soyadınız"
                          />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>E-posta</FormLabel>
                          <InputGroup>
                            <Input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="E-posta adresiniz"
                            />
                            <InputRightElement>
                              <Icon as={FiMail} color="gray.400" />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Telefon</FormLabel>
                          <InputGroup>
                            <Input
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              placeholder="Telefon numaranız"
                            />
                            <InputRightElement>
                              <Icon as={FiPhone} color="gray.400" />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>
                      </GridItem>
                    </Grid>

                    <Button
                      colorScheme="blue"
                      onClick={handleUpdateProfile}
                      isLoading={isLoading}
                      leftIcon={<FiUser />}
                    >
                      Profil Bilgilerini Güncelle
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            </Box>
          </Box>

          <Box display={view === "sifre" ? "block" : "none"}>
            <Card>
              <CardHeader>
                <HStack>
                  <Icon as={FiLock} color="green.500" />
                  <Heading size="md">Şifre Değiştir</Heading>
                </HStack>
              </CardHeader>
              <CardBody>
                <VStack spacing={6}>
                  {/* Current Password */}
                  <FormControl isInvalid={errorData.old_password}>
                    <FormLabel>Mevcut Şifre</FormLabel>
                    <InputGroup>
                      <Input
                        type={showOldPassword ? "text" : "password"}
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        placeholder="Mevcut şifrenizi girin"
                      />
                      <InputRightElement>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowOldPassword(!showOldPassword)}
                          icon={showOldPassword ? <FiEyeOff /> : <FiEye />}
                        />
                      </InputRightElement>
                    </InputGroup>
                    {errorData.old_password && (
                      <FormErrorMessage>
                        {errorData.old_password.join(", ")}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  {/* New Password */}
                  <FormControl>
                    <FormLabel>Yeni Şifre</FormLabel>
                    <InputGroup>
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={password1}
                        onChange={(e) => setPassword1(e.target.value)}
                        placeholder="Yeni şifrenizi girin"
                      />
                      <InputRightElement>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          icon={showNewPassword ? <FiEyeOff /> : <FiEye />}
                        />
                      </InputRightElement>
                    </InputGroup>

                    {/* Password Strength Indicator */}
                    {password1 && (
                      <Box mt={3}>
                        <HStack justify="space-between" mb={2}>
                          <Text fontSize="sm" color="gray.600">
                            Şifre Gücü:
                          </Text>
                          <Badge
                            colorScheme={strengthInfo.color.split(".")[0]}
                            variant="subtle"
                          >
                            {strengthInfo.text}
                          </Badge>
                        </HStack>
                        <Progress
                          value={passwordStrength.score}
                          colorScheme={strengthInfo.color.split(".")[0]}
                          size="sm"
                          borderRadius="md"
                        />
                      </Box>
                    )}
                  </FormControl>

                  {/* Password Requirements */}
                  {password1 && (
                    <Box w="full">
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        mb={3}
                        color="gray.700"
                      >
                        Şifre Gereksinimleri:
                      </Text>
                      <List spacing={2}>
                        <ListItem>
                          <ListIcon
                            as={passwordStrength.checks.length ? FiCheck : FiX}
                            color={
                              passwordStrength.checks.length
                                ? "green.500"
                                : "red.500"
                            }
                          />
                          <Text as="span" fontSize="sm">
                            En az 8 karakter
                          </Text>
                        </ListItem>
                        <ListItem>
                          <ListIcon
                            as={
                              passwordStrength.checks.uppercase ? FiCheck : FiX
                            }
                            color={
                              passwordStrength.checks.uppercase
                                ? "green.500"
                                : "red.500"
                            }
                          />
                          <Text as="span" fontSize="sm">
                            En az bir büyük harf (A-Z)
                          </Text>
                        </ListItem>
                        <ListItem>
                          <ListIcon
                            as={
                              passwordStrength.checks.lowercase ? FiCheck : FiX
                            }
                            color={
                              passwordStrength.checks.lowercase
                                ? "green.500"
                                : "red.500"
                            }
                          />
                          <Text as="span" fontSize="sm">
                            En az bir küçük harf (a-z)
                          </Text>
                        </ListItem>
                        <ListItem>
                          <ListIcon
                            as={passwordStrength.checks.number ? FiCheck : FiX}
                            color={
                              passwordStrength.checks.number
                                ? "green.500"
                                : "red.500"
                            }
                          />
                          <Text as="span" fontSize="sm">
                            En az bir rakam (0-9)
                          </Text>
                        </ListItem>
                        <ListItem>
                          <ListIcon
                            as={passwordStrength.checks.special ? FiCheck : FiX}
                            color={
                              passwordStrength.checks.special
                                ? "green.500"
                                : "red.500"
                            }
                          />
                          <Text as="span" fontSize="sm">
                            En az bir özel karakter (!@#$%^&*)
                          </Text>
                        </ListItem>
                      </List>
                    </Box>
                  )}

                  {/* Confirm Password */}
                  <FormControl isInvalid={password1 !== password2 && password2}>
                    <FormLabel>Yeni Şifre Tekrarı</FormLabel>
                    <InputGroup>
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        value={password2}
                        onChange={(e) => setPassword2(e.target.value)}
                        placeholder="Yeni şifrenizi tekrar girin"
                      />
                      <InputRightElement>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          icon={showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                        />
                      </InputRightElement>
                    </InputGroup>
                    {password1 !== password2 && password2 && (
                      <FormErrorMessage>Şifreler eşleşmiyor</FormErrorMessage>
                    )}
                    {errorData.new_password && (
                      <FormErrorMessage>
                        {errorData.new_password.join(", ")}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <Button
                    colorScheme="green"
                    onClick={handlePasswordReset}
                    isLoading={isLoading}
                    isDisabled={
                      !oldPassword ||
                      !password1 ||
                      !password2 ||
                      password1 !== password2
                    }
                    leftIcon={<FiLock />}
                  >
                    Şifreyi Değiştir
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          </Box>

          <Box display={view === "uygulama" ? "block" : "none"}>
            <Card>
              <CardHeader>
                <HStack>
                  <Icon as={FiSettings} color="blue.500" />
                  <Heading size="md">Uygulama Ayarları</Heading>
                </HStack>
              </CardHeader>
              <CardBody>
                <Accordion allowMultiple>
                  <AccordionItem>
                    <h2>
                      <AccordionButton>
                        <Box as="span" flex="1" textAlign="left">
                          Genel
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                      <VStack spacing={4} align="stretch">
                        <FormControl>
                          <FormLabel>Dil</FormLabel>
                          <Select
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                          >
                            <option value="tr">Türkçe</option>
                            <option value="en">English</option>
                          </Select>
                          <FormHelperText>
                            Uygulama dilini değiştirin
                          </FormHelperText>
                        </FormControl>
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>

                  <AccordionItem>
                    <h2>
                      <AccordionButton>
                        <Box as="span" flex="1" textAlign="left">
                          Görünüm
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                      <VStack spacing={4} align="stretch">
                        <FormControl
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <FormLabel mb={0}>Karanlık Mod</FormLabel>
                          <Switch
                            aria-label="Karanlık Mod"
                            isChecked={darkMode}
                            onChange={(e) =>
                              setDarkPreference(e.target.checked)
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Yazı Boyutu</FormLabel>
                          <Select
                            value={fontSize}
                            onChange={(e) => setFontSize(e.target.value)}
                          >
                            <option value="small">Küçük</option>
                            <option value="medium">Orta</option>
                            <option value="large">Büyük</option>
                          </Select>
                          <FormHelperText>
                            Metinleri okuma rahatlığınıza göre ayarlayın
                          </FormHelperText>
                        </FormControl>
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>

                  <AccordionItem>
                    <h2>
                      <AccordionButton>
                        <Box as="span" flex="1" textAlign="left">
                          Bildirimler
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                      <VStack spacing={4} align="stretch">
                        <Box bg="gray.50" borderRadius="md" p={3}>
                          <Text
                            fontSize="sm"
                            color="gray.700"
                            fontWeight="medium"
                          >
                            {notifChannelsSummary()}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {notifTriggersSummary()}
                          </Text>
                        </Box>
                        <FormControl
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <FormLabel mb={0}>E-posta Bildirimleri</FormLabel>
                          <Switch
                            aria-label="E-posta"
                            isChecked={notifEmail}
                            onChange={(e) => setNotifEmail(e.target.checked)}
                          />
                        </FormControl>
                        <FormControl
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <FormLabel mb={0}>SMS Bildirimleri</FormLabel>
                          <Switch
                            aria-label="SMS"
                            isChecked={notifSms}
                            onChange={(e) => setNotifSms(e.target.checked)}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Bildirim Koşulları</FormLabel>
                          <CheckboxGroup
                            value={notifTriggers}
                            onChange={setNotifTriggers}
                          >
                            <HStack spacing={4} wrap="wrap">
                              <Checkbox value="ticket_created">
                                İş Emri Oluşturuldu
                              </Checkbox>
                              <Checkbox value="status_changed">
                                Durum Değişti
                              </Checkbox>
                              <Checkbox value="assignment">
                                Atama Yapıldı
                              </Checkbox>
                              <Checkbox value="completed">Tamamlandı</Checkbox>
                              <Checkbox value="cancelled">
                                İptal Edildi
                              </Checkbox>
                              <Checkbox value="appointment_set">
                                Randevu Oluşturuldu
                              </Checkbox>
                              <Checkbox value="appointment_updated">
                                Randevu Güncellendi
                              </Checkbox>
                              <Checkbox value="appointment_cancelled">
                                Randevu İptal
                              </Checkbox>
                              <Checkbox value="incomplete_tickets">
                                Eksik İş Emri Uyarıları
                              </Checkbox>
                            </HStack>
                          </CheckboxGroup>
                          <FormHelperText>
                            Hangi durumlarda SMS/E-posta alacağınızı seçin
                          </FormHelperText>
                        </FormControl>
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={selectAllTriggers}
                          >
                            Hepsini Seç
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={selectCriticalTriggers}
                          >
                            Sadece Kritikler
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={clearTriggers}
                          >
                            Temizle
                          </Button>
                        </HStack>
                        <HStack spacing={3}>
                          <Button
                            colorScheme="blue"
                            onClick={saveNotificationSettings}
                            isLoading={notifSaving}
                          >
                            Kaydet
                          </Button>
                          <Button
                            variant="outline"
                            onClick={sendTestNotification}
                            isLoading={notifTesting}
                          >
                            Test Bildirimi Gönder
                          </Button>
                        </HStack>
                        <FormControl
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <FormLabel mb={0}>Rozet Göster</FormLabel>
                          <Switch
                            aria-label="Bildirim Rozeti"
                            isChecked={showBadge}
                            onChange={(e) => setShowBadge(e.target.checked)}
                          />
                        </FormControl>
                        <FormControl
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <FormLabel mb={0}>Bildirim Sesi</FormLabel>
                          <Switch
                            aria-label="Bildirim Sesi"
                            isChecked={soundEnabled}
                            onChange={(e) => setSoundEnabled(e.target.checked)}
                          />
                        </FormControl>
                        <FormControl
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <FormLabel mb={0}>Sessiz Saatler</FormLabel>
                          <Switch
                            aria-label="Sessiz Saatler"
                            isChecked={quietEnabled}
                            onChange={(e) => setQuietEnabled(e.target.checked)}
                          />
                        </FormControl>
                        <Grid
                          templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                          gap={4}
                        >
                          <GridItem>
                            <FormControl>
                              <FormLabel>Sessiz Başlangıç</FormLabel>
                              <Input
                                type="time"
                                value={quietStart}
                                onChange={(e) => setQuietStart(e.target.value)}
                              />
                              <FormHelperText>
                                Günün başlangıç saati
                              </FormHelperText>
                            </FormControl>
                          </GridItem>
                          <GridItem>
                            <FormControl>
                              <FormLabel>Sessiz Bitiş</FormLabel>
                              <Input
                                type="time"
                                value={quietEnd}
                                onChange={(e) => setQuietEnd(e.target.value)}
                              />
                              <FormHelperText>Günün bitiş saati</FormHelperText>
                            </FormControl>
                          </GridItem>
                        </Grid>
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>

                  <AccordionItem>
                    <h2>
                      <AccordionButton>
                        <Box as="span" flex="1" textAlign="left">
                          Güvenlik
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                      <VStack spacing={4} align="stretch">
                        <FormControl
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <FormLabel mb={0}>İki Aşamalı Doğrulama</FormLabel>
                          <Switch
                            aria-label="İki Aşamalı"
                            isChecked={twoFAEnabled}
                            onChange={(e) => setTwoFAEnabled(e.target.checked)}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Doğrulama Kanalı</FormLabel>
                          <Select
                            value={twoFAChannel}
                            onChange={(e) => setTwoFAChannel(e.target.value)}
                          >
                            <option value="sms">SMS</option>
                            <option value="email">E-posta</option>
                          </Select>
                          <FormHelperText>
                            Girişte doğrulama kodunu hangi kanalla almak
                            istediğinizi seçin
                          </FormHelperText>
                        </FormControl>
                        <HStack>
                          <Button
                            colorScheme="blue"
                            onClick={saveSecuritySettings}
                          >
                            Kaydet
                          </Button>
                        </HStack>
                        <Divider />
                        <Heading size="sm">Kayıtlı Cihazlar</Heading>
                        <HStack>
                          <Button size="sm" onClick={fetchTrustedDevices} isLoading={trustedLoading}>
                            Yenile
                          </Button>
                        </HStack>
                        <VStack align="stretch" spacing={3}>
                          {(trustedDevices || []).map((d) => (
                            <HStack key={`${d.id}-${d.fingerprint}`} spacing={4} justify="space-between" p={3} borderWidth="1px" borderRadius="md">
                              <VStack align="start" spacing={0}>
                                <HStack spacing={2}>
                                  <Text fontWeight="semibold">{d.name || "Tarayıcı"}</Text>
                                  <Badge colorScheme={d.trusted ? "green" : "gray"}>{d.trusted ? "Güvenilir" : "Kayıtlı"}</Badge>
                                </HStack>
                                <Text fontSize="sm" color="gray.600">{d.last_ip || "-"}</Text>
                                <Text fontSize="xs" color="gray.500">{d.fingerprint.slice(0, 16)}…</Text>
                              </VStack>
                              <VStack align="end" spacing={1}>
                                <HStack spacing={2}>
                                  <Badge colorScheme="purple" variant="subtle">{d.login_count || 0} giriş</Badge>
                                  <Text fontSize="xs" color="gray.500">{d.last_seen ? String(d.last_seen).replace("T", " ").slice(0, 19) : ""}</Text>
                                </HStack>
                                <Button size="sm" colorScheme="red" variant="outline" onClick={() => removeTrusted(d.fingerprint)}>
                                  Kaldır
                                </Button>
                              </VStack>
                            </HStack>
                          ))}
                          {(!trustedDevices || trustedDevices.length === 0) && (
                            <Text fontSize="sm" color="gray.500">Kayıtlı cihaz bulunmuyor</Text>
                          )}
                        </VStack>
                        <Divider />
                        <Heading size="sm">İletişim</Heading>
                        <FormControl>
                          <FormLabel>E-posta</FormLabel>
                          <Input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Telefon</FormLabel>
                          <Input
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                          />
                        </FormControl>
                        <HStack>
                          <Button
                            variant="outline"
                            onClick={handleUpdateProfile}
                          >
                            İletişim Bilgilerini Kaydet
                          </Button>
                        </HStack>
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>

                  <AccordionItem>
                    <h2>
                      <AccordionButton>
                        <Box as="span" flex="1" textAlign="left">
                          Listeler
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                      <VStack spacing={4} align="stretch">
                        <FormControl>
                          <FormLabel>Sayfa Boyutu</FormLabel>
                          <Select
                            value={String(pageSize)}
                            onChange={(e) =>
                              setPageSize(parseInt(e.target.value, 10))
                            }
                          >
                            <option value="0">Varsayılan</option>
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </Select>
                          <FormHelperText>Liste satır sayısı</FormHelperText>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Varsayılan Sıralama</FormLabel>
                          <Select
                            value={defaultSort}
                            onChange={(e) => setDefaultSort(e.target.value)}
                          >
                            <option value="created_at:desc">
                              Oluşturulma ↓
                            </option>
                            <option value="created_at:asc">
                              Oluşturulma ↑
                            </option>
                            <option value="updated_at:desc">
                              Güncellenme ↓
                            </option>
                            <option value="updated_at:asc">
                              Güncellenme ↑
                            </option>
                          </Select>
                        </FormControl>

                        <FormControl
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        ></FormControl>
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </CardBody>
            </Card>
          </Box>

          {/* Security Info Card */}
          <Card>
            <CardHeader>
              <HStack>
                <Icon as={FiShield} color="purple.500" />
                <Heading size="md">Güvenlik Bilgileri</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="start">
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Güvenlik İpuçları:
                  </Text>
                  <List spacing={2}>
                    <ListItem>
                      <ListIcon as={FiCheck} color="green.500" />
                      <Text as="span" fontSize="sm">
                        Şifrenizi düzenli olarak değiştirin
                      </Text>
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FiCheck} color="green.500" />
                      <Text as="span" fontSize="sm">
                        Şifrenizi başkalarıyla paylaşmayın
                      </Text>
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FiCheck} color="green.500" />
                      <Text as="span" fontSize="sm">
                        Güçlü ve benzersiz şifreler kullanın
                      </Text>
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FiCheck} color="green.500" />
                      <Text as="span" fontSize="sm">
                        Şüpheli aktiviteleri bildirin
                      </Text>
                    </ListItem>
                  </List>
                </Box>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </Box>
  );
}
